import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'
import type { GridFieldType } from '@/features/Inventory/ObjectTypePassportGrid/model/types'
import { normalizeTableLayout } from './tableLayout'
import { max } from 'lodash'
import { itemBelongsToTabScope, stampItemForTabScope, type PassportTabScope } from './tabGrid'

export const PASSPORT_GRID_COLS = 12

export type GridRowGroup = {
  row: number
  items: GridFieldType[]
}

type GridFieldDraft = Pick<GridFieldType, 'id' | 'name'> &
  Partial<Omit<GridFieldType, 'id' | 'name' | 'row' | 'cell'>>

/**
 * Grid CRUD for `passport_grid_template` (12-column rows). Mutations always touch the full `gridTemplate`;
 * `tabScope` filters what the UI shows and sets `group_id` / `builder_tab_ref` for new placements.
 */
export function useGridOperations(
  gridTemplate: Ref<GridFieldType[]>,
  tabScope: Ref<PassportTabScope | null>,
  /** LIST group ids: their cells show on the synthetic tab because LIST is not a canvas tab. */
  listGroupIds?: ComputedRef<Set<number>>,
) {
  const minVisualRows = ref(4)

  function belongs(f: GridFieldType, scope: PassportTabScope) {
    return itemBelongsToTabScope(f, scope, listGroupIds?.value)
  }

  function visibleItems(): GridFieldType[] {
    const scope = tabScope.value
    if (!scope) return []
    return gridTemplate.value.filter((f) => belongs(f, scope))
  }

  watch(
    () => [gridTemplate.value, tabScope.value, listGroupIds?.value] as const,
    () => {
      const scope = tabScope.value
      if (!scope) return
      const vis = gridTemplate.value.filter((f) => belongs(f, scope))
      const maxR = vis.length ? max(vis.map((f) => f.row))! : -1
      minVisualRows.value = Math.max(4, maxR + 1, 1)
    },
    { deep: true, immediate: true },
  )

  watch(
    () => tabScope.value,
    () => {
      minVisualRows.value = 4
    },
  )

  const rowsGrouped = computed<GridRowGroup[]>(() => {
    const scope = tabScope.value
    if (!scope) return []
    const byRow = new Map<number, GridFieldType[]>()
    for (const item of gridTemplate.value) {
      if (!belongs(item, scope)) continue
      const list = byRow.get(item.row) ?? []
      list.push(item)
      byRow.set(item.row, list)
    }
    for (const [, list] of byRow) {
      list.sort((a, b) => a.cell - b.cell || a.id.localeCompare(b.id))
    }
    const maxFromFields = byRow.size ? max([...byRow.keys()])! : -1
    const lastRow = Math.max(maxFromFields, minVisualRows.value - 1)
    const result: GridRowGroup[] = []
    for (let r = 0; r <= lastRow; r++) {
      result.push({ row: r, items: byRow.get(r) ?? [] })
    }
    return result
  })

  const maxRow = computed(() => (rowsGrouped.value.length ? rowsGrouped.value.length - 1 : 0))

  function getRowItems(row: number, excludeId?: string): GridFieldType[] {
    const scope = tabScope.value
    if (!scope) return []
    return gridTemplate.value.filter(
      (f) => f.row === row && f.id !== excludeId && belongs(f, scope),
    )
  }

  const addRow = () => {
    minVisualRows.value = Math.max(minVisualRows.value, maxRow.value + 2)
  }

  const removeRow = (rowIndex: number) => {
    const scope = tabScope.value
    if (!scope) return
    gridTemplate.value = gridTemplate.value
      .filter((item) => !(item.row === rowIndex && belongs(item, scope)))
      .map((item) =>
        belongs(item, scope) && item.row > rowIndex
          ? { ...item, row: item.row - 1 }
          : item,
      )
    minVisualRows.value = Math.max(1, minVisualRows.value - 1)
  }

  function stampIfScoped(item: GridFieldType): GridFieldType {
    const scope = tabScope.value
    if (!scope) return item
    return stampItemForTabScope(item, scope)
  }

  const addFieldToRow = (row: number, partial: Omit<GridFieldType, 'row'> & { row?: number }) => {
    const scope = tabScope.value
    if (!scope) return false
    const raw: GridFieldType = { ...partial, row, cell: partial.cell, length: partial.length }
    const item = stampIfScoped(raw)
    gridTemplate.value = [...gridTemplate.value, item]
    return true
  }

  /**
   * Removes a single grid cell. When `row` + `cell` are set, only that placement is removed
   * (same `id` may appear in multiple cells if the same palette field was dropped more than once).
   */
  const removeField = (id: string, row?: number, cell?: number) => {
    const idx = gridTemplate.value.findIndex((f) => {
      if (f.id !== id) return false
      if (row !== undefined && cell !== undefined) {
        return f.row === row && f.cell === cell
      }
      return true
    })
    if (idx === -1) return
    gridTemplate.value = gridTemplate.value.filter((_, i) => i !== idx)
  }

  const resizeField = (id: string, length: number, row?: number, cell?: number) => {
    const nextLen = Math.max(1, Math.min(PASSPORT_GRID_COLS, length))
    gridTemplate.value = gridTemplate.value.map((f) => {
      if (f.id !== id) return f
      if (row !== undefined && cell !== undefined && (f.row !== row || f.cell !== cell)) return f
      return { ...f, length: nextLen }
    })
    return true
  }

  /** Rename a single grid item (e.g. heading bar); scoped by row/cell when duplicates share id. */
  const renameItemName = (id: string, name: string, row?: number, cell?: number) => {
    const trimmed = name.trim()
    if (!trimmed) return false
    const hasTarget = gridTemplate.value.some((f) => {
      if (f.id !== id) return false
      if (row !== undefined && cell !== undefined) return f.row === row && f.cell === cell
      return true
    })
    if (!hasTarget) return false
    gridTemplate.value = gridTemplate.value.map((f) => {
      if (f.id !== id) return f
      if (row !== undefined && cell !== undefined && (f.row !== row || f.cell !== cell)) return f
      return { ...f, name: trimmed }
    })
    return true
  }

  const addHeading = (row: number, cell: number, length: number, name: string) => {
    const scope = tabScope.value
    if (!scope) return false
    const raw: GridFieldType = {
      id: crypto.randomUUID(),
      type: 'heading',
      name,
      row,
      cell,
      length,
    }
    gridTemplate.value = [...gridTemplate.value, stampItemForTabScope(raw, scope)]
    return true
  }

  /** Full-width LIST / перечень table block (12 cols), same row if empty else new row below. */
  const addTableBlock = (rowNum: number, drag: { name: string; list_group_id?: number }) => {
    const scope = tabScope.value
    if (!scope) return false
    const id = crypto.randomUUID()
    const listGroupId =
      drag.list_group_id != null && Number.isFinite(Number(drag.list_group_id))
        ? Number(drag.list_group_id)
        : undefined
    const partial: Omit<GridFieldType, 'row'> = {
      id,
      type: 'table',
      name: drag.name,
      cell: 0,
      length: PASSPORT_GRID_COLS,
      table_name_span: 11,
      table_columns: [],
      ...(listGroupId != null ? { list_group_id: listGroupId } : {}),
    }
    const used = getRowItems(rowNum).reduce((s, f) => s + f.length, 0)
    if (used === 0) {
      gridTemplate.value = [
        ...gridTemplate.value,
        stampItemForTabScope({ ...partial, row: rowNum } as GridFieldType, scope),
      ]
      return true
    }
    placeOnNewRowBelow(
      rowNum,
      {
        id,
        name: drag.name,
        type: 'table',
        table_name_span: 11,
        table_columns: [],
        ...(listGroupId != null ? { list_group_id: listGroupId } : {}),
      },
      PASSPORT_GRID_COLS,
    )
    return true
  }

  const patchTableField = (tableFieldId: string, updater: (f: GridFieldType) => GridFieldType) => {
    gridTemplate.value = gridTemplate.value.map((f) =>
      String(f.id) === String(tableFieldId) && f.type === 'table' ? normalizeTableLayout(updater(f)) : f,
    )
  }

  const moveField = (id: string, newRow: number, newCell: number) => {
    const scope = tabScope.value
    if (!scope) return false
    gridTemplate.value = gridTemplate.value.map((f) => {
      if (f.id !== id) return f
      return stampItemForTabScope({ ...f, row: newRow, cell: newCell }, scope)
    })
    return true
  }

  const placementKey = (item: GridFieldType): string => `${item.id}:${item.row}:${item.cell}`

  const reorderInRow = (
    rowNum: number,
    fieldId: string,
    targetIndex: number,
    source?: { row?: number; cell?: number },
  ) => {
    const items = getRowItems(rowNum).sort((a, b) => a.cell - b.cell)
    const fromIdx = items.findIndex((f) => {
      if (f.id !== fieldId) return false
      if (source?.row !== undefined && source?.cell !== undefined) {
        return f.row === source.row && f.cell === source.cell
      }
      return true
    })
    if (fromIdx === -1) return false
    if (fromIdx === targetIndex) return false

    const moved = items.splice(fromIdx, 1)[0]
    const clampedIdx = Math.max(0, Math.min(items.length, targetIndex))
    items.splice(clampedIdx, 0, moved)

    repackRow(items, rowNum)
    return true
  }

  function repackRow(sortedItems: GridFieldType[], rowNum: number) {
    let cursor = 0
    const updates = new Map<string, number>()
    for (const item of sortedItems) {
      updates.set(placementKey(item), cursor)
      cursor += item.length
    }
    gridTemplate.value = gridTemplate.value.map((f) => {
      if (f.row !== rowNum) return f
      const newCell = updates.get(placementKey(f))
      return newCell !== undefined ? { ...f, cell: newCell } : f
    })
  }

  const smartPlaceField = (
    rowNum: number,
    newField: GridFieldDraft & { length: number },
  ): number => {
    const scope = tabScope.value
    if (!scope) return rowNum
    const existing = getRowItems(rowNum, newField.id)
    const used = existing.reduce((s, f) => s + f.length, 0)
    const desiredSpan = Math.max(1, Math.min(PASSPORT_GRID_COLS, newField.length))
    if (newField.view_type === 'table') {
      if (used === 0) {
        addFieldToRow(rowNum, {
          ...newField,
          cell: 0,
          length: PASSPORT_GRID_COLS,
        } as Omit<GridFieldType, 'row'> & { row?: number })
        return rowNum
      }

      return placeOnNewRowBelow(rowNum, newField, PASSPORT_GRID_COLS)
    }

    const free = PASSPORT_GRID_COLS - used

    if (free >= desiredSpan) {
      const nextCell = used
      addFieldToRow(rowNum, {
        ...newField,
        cell: nextCell,
        length: desiredSpan,
      } as Omit<GridFieldType, 'row'> & { row?: number })
      repackRow(
        getRowItems(rowNum).sort((a, b) => a.cell - b.cell),
        rowNum,
      )
      return rowNum
    }

    if (free >= 1) {
      const nextCell = used
      addFieldToRow(rowNum, {
        ...newField,
        cell: nextCell,
        length: free,
      } as Omit<GridFieldType, 'row'> & { row?: number })
      repackRow(
        getRowItems(rowNum).sort((a, b) => a.cell - b.cell),
        rowNum,
      )
      return rowNum
    }

    return placeOnNewRowBelow(rowNum, newField, desiredSpan)
  }

  function placeOnNewRowBelow(
    afterRow: number,
    newField: GridFieldDraft,
    span: number,
  ): number {
    const scope = tabScope.value
    if (!scope) return afterRow + 1
    const newRow = afterRow + 1
    gridTemplate.value = gridTemplate.value.map((f) =>
      belongs(f, scope) && f.row >= newRow ? { ...f, row: f.row + 1 } : f,
    )
    minVisualRows.value = Math.max(minVisualRows.value, minVisualRows.value + 1)
    addFieldToRow(newRow, { ...newField, cell: 0, length: span } as Omit<GridFieldType, 'row'> & {
      row?: number
    })
    return newRow
  }

  return {
    rowsGrouped,
    maxRow,
    addRow,
    removeRow,
    addFieldToRow,
    removeField,
    resizeField,
    renameItemName,
    addHeading,
    addTableBlock,
    patchTableField,
    moveField,
    reorderInRow,
    smartPlaceField,
    getRowItems,
    visibleItems,
  }
}
